import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
  type Firestore,
} from 'firebase/firestore';
import type {
  CreateProfileComponentInput,
  CreateProfileInput,
  Profile,
  ProfileComponent,
  ProfileRepository,
  Unsubscribe,
  UpdateProfileComponentInput,
  UpdateProfileInput,
} from '@nx/domain';
import {
  DEFAULT_PROFILE_ROLE_TYPE,
  DEFAULT_THEME_CONFIG,
  normalizeProfileOptionalText,
} from '@nx/domain';
import {
  mapProfileComponentDoc,
  mapProfileDoc,
  snapshotData,
} from './profileMappers';

const PROFILES = 'profiles';

export class FirebaseProfileRepository implements ProfileRepository {
  constructor(private readonly db: Firestore) {}

  async getByOwner(ownerId: string): Promise<Profile | null> {
    const q = query(collection(this.db, PROFILES), where('ownerId', '==', ownerId));
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const sorted = snap.docs
      .map((d) => ({ doc: d, updatedAt: (d.data().updatedAt as { toMillis?: () => number })?.toMillis?.() ?? 0 }))
      .sort((a, b) => b.updatedAt - a.updatedAt);
    const first = sorted[0].doc;
    return mapProfileDoc(first.id, snapshotData(first));
  }

  async getById(profileId: string): Promise<Profile | null> {
    const snap = await getDoc(doc(this.db, PROFILES, profileId));
    if (!snap.exists()) return null;
    return mapProfileDoc(snap.id, snapshotData(snap));
  }

  async getBySlugPublished(slug: string): Promise<Profile | null> {
    const q = query(
      collection(this.db, PROFILES),
      where('slug', '==', slug),
      where('status', '==', 'published'),
    );
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const first = snap.docs[0];
    return mapProfileDoc(first.id, snapshotData(first));
  }

  async create(ownerId: string, input: CreateProfileInput): Promise<Profile> {
    const themeConfig = { ...DEFAULT_THEME_CONFIG, ...input.themeConfig };
    const ref = await addDoc(collection(this.db, PROFILES), {
      ownerId,
      slug: input.slug ?? '',
      title: input.title.trim(),
      description: input.description?.trim() ?? null,
      tagline: normalizeProfileOptionalText(input.tagline),
      location: normalizeProfileOptionalText(input.location),
      avatarUrl: input.avatarUrl ?? null,
      roleType: input.roleType ?? DEFAULT_PROFILE_ROLE_TYPE,
      tags: input.tags ?? [],
      status: 'draft',
      themeConfig,
      viewCount: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    const created = await this.getById(ref.id);
    if (!created) throw new Error('Failed to load created profile');
    return created;
  }

  async update(profileId: string, input: UpdateProfileInput): Promise<Profile> {
    const patch: Record<string, unknown> = { updatedAt: serverTimestamp() };
    if (input.title !== undefined) patch.title = input.title.trim();
    if (input.description !== undefined) patch.description = input.description?.trim() ?? null;
    if (input.tagline !== undefined) patch.tagline = normalizeProfileOptionalText(input.tagline);
    if (input.location !== undefined) patch.location = normalizeProfileOptionalText(input.location);
    if (input.avatarUrl !== undefined) patch.avatarUrl = input.avatarUrl;
    if (input.roleType !== undefined) patch.roleType = input.roleType;
    if (input.tags !== undefined) patch.tags = input.tags;
    if (input.status !== undefined) patch.status = input.status;
    if (input.themeConfig !== undefined) {
      const current = await this.getById(profileId);
      patch.themeConfig = { ...(current?.themeConfig ?? DEFAULT_THEME_CONFIG), ...input.themeConfig };
    }
    await updateDoc(doc(this.db, PROFILES, profileId), patch);
    const updated = await this.getById(profileId);
    if (!updated) throw new Error('Profile not found after update');
    return updated;
  }

  async listComponents(profileId: string): Promise<ProfileComponent[]> {
    const snap = await getDocs(collection(this.db, PROFILES, profileId, 'components'));
    return snap.docs
      .map((d) => mapProfileComponentDoc(profileId, d.id, snapshotData(d)))
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }

  watchComponents(
    profileId: string,
    listener: (components: ProfileComponent[]) => void,
  ): Unsubscribe {
    const q = collection(this.db, PROFILES, profileId, 'components');
    return onSnapshot(q, (snap) => {
      const items = snap.docs
        .map((d) => mapProfileComponentDoc(profileId, d.id, snapshotData(d)))
        .sort((a, b) => a.sortOrder - b.sortOrder);
      listener(items);
    });
  }

  async addComponent(
    profileId: string,
    input: CreateProfileComponentInput,
  ): Promise<ProfileComponent> {
    const existing = await getDocs(collection(this.db, PROFILES, profileId, 'components'));
    const maxOrder = existing.docs.reduce((max, d) => {
      const order = (d.data().sortOrder as number | undefined) ?? 0;
      return Math.max(max, order);
    }, -1);
    const ref = await addDoc(collection(this.db, PROFILES, profileId, 'components'), {
      type: input.type,
      config: input.config,
      visible: input.visible !== false,
      sortOrder: input.sortOrder ?? maxOrder + 1,
      clickCount: 0,
      createdAt: serverTimestamp(),
    });
    const snap = await getDoc(ref);
    return mapProfileComponentDoc(profileId, snap.id, snapshotData(snap));
  }

  async updateComponent(
    profileId: string,
    componentId: string,
    input: UpdateProfileComponentInput,
  ): Promise<ProfileComponent> {
    const ref = doc(this.db, PROFILES, profileId, 'components', componentId);
    const currentSnap = await getDoc(ref);
    if (!currentSnap.exists()) throw new Error('Component not found');
    const current = mapProfileComponentDoc(profileId, currentSnap.id, snapshotData(currentSnap));
    const patch: Record<string, unknown> = {};
    if (input.type !== undefined) patch.type = input.type;
    if (input.visible !== undefined) patch.visible = input.visible;
    if (input.sortOrder !== undefined) patch.sortOrder = input.sortOrder;
    if (input.config !== undefined) {
      patch.config = input.replaceConfig
        ? input.config
        : { ...current.config, ...input.config };
    }
    await updateDoc(ref, patch);
    const updatedSnap = await getDoc(ref);
    return mapProfileComponentDoc(profileId, updatedSnap.id, snapshotData(updatedSnap));
  }

  async removeComponent(profileId: string, componentId: string): Promise<void> {
    await deleteDoc(doc(this.db, PROFILES, profileId, 'components', componentId));
  }

  async reorderComponents(profileId: string, orderedIds: string[]): Promise<void> {
    const batch = writeBatch(this.db);
    orderedIds.forEach((id, index) => {
      batch.update(doc(this.db, PROFILES, profileId, 'components', id), { sortOrder: index });
    });
    await batch.commit();
  }

  async delete(profileId: string): Promise<void> {
    const componentsSnap = await getDocs(collection(this.db, PROFILES, profileId, 'components'));
    const batch = writeBatch(this.db);
    componentsSnap.docs.forEach((d) => {
      batch.delete(d.ref);
    });
    batch.delete(doc(this.db, PROFILES, profileId));
    await batch.commit();
  }
}
